import React, { useRef, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import {
  TooltipComponent,
  TitleComponent,
  GridComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';
import dayjs from 'dayjs';

echarts.use([
  TooltipComponent,
  TitleComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);
const getDefaultOptions = (theme, getThemeColor, data, unit) => {
  // Extract machines and their values

  const machines = Object.keys(data);

  // Aggregate values for each machine
  const seriesData = machines.map(machine => {
    // Sum all values for the machine (or use another aggregation logic if needed)
    const totalValue = data[machine].reduce((sum, value) => sum + value[1], 0);

    return totalValue;
  });

  return {
    color: [getThemeColor('primary')], // Single color for the bars
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'shadow'
      },
      formatter: params => {
        return params
          .map(
            ({ axisValue, value }) =>
              `<div>${axisValue}: ${value} ${unit}</div>`
          )
          .join('');
      }
    },
    xAxis: {
      type: 'category',
      data: machines, // Machine names as categories
      axisLabel: {
        color: getThemeColor('gray-800'),
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12.8,
        interval: 0, // Display all labels
        align: 'center'
      },
      axisLine: {
        lineStyle: {
          color: getThemeColor('gray-300')
        }
      },
      axisTick: {
        alignWithLabel: true
      }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('gray-300') }
      },
      axisLabel: {
        color: getThemeColor('gray-900'),
        formatter: `{value} ${unit}`
      }
    },
    series: [
      {
        name: 'Machine Data',
        type: 'bar',
        data: seriesData, // Values corresponding to machine names
        barWidth: '12px',
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('primary')
        }
      }
    ],
    grid: {
      right: 40,
      left: 20,
      bottom: '30px', // Adjust to accommodate labels
      top: '10%',
      containLabel: true
    },
    animation: false,
    barCategoryGap: '50%' // Increase gap between bar categories
  };
};

const DynamicBarChart = ({ data, title, unit }) => {
  const chartRef = useRef(null);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(theme, getThemeColor, data, unit)}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold'
  }
};

export default memo(DynamicBarChart);
